import * as React from 'react';
import { CaretUp, CaretDown, ArrowsDownUp } from '../../../design-system/icons';
import type { Table as TanstackTable } from '@tanstack/react-table';
import type { ColumnDef, RowSelectionConfig } from './types';
import { cn } from '../../../lib/utils';
import { TableHeader, TableRow, TableHead } from '../../ui/table';
import { Checkbox } from '../../ui/checkbox';
import {
  colWidthStyle,
  distributedPaddingStyle,
  needsTrailingSpacer,
  stickyLeftOffset,
} from './DataTable';
import { SELECTION_COL_WIDTH } from './constants';
import { selectableIds, selectAllState, toggleSelectAll } from './rowSelection';
import { sortButtonAlignClass, textAlignClass } from './columnAlign';

interface DataTableHeaderProps<T> {
  /** TanStack instance — owns the sort state + toggle cycle for each column. */
  table: TanstackTable<T>;
  columns: ColumnDef<T>[];
  /** Measured per-boundary gap from the body — see useDistributedGap in
   *  DataTable.tsx. `null` means "not active", falls back to CELL_X. */
  perBoundaryPx?: number | null;
  rowSelection?: RowSelectionConfig<T>;
  data?: T[];
  getRowId?: (row: T) => string;
}

/** Pixel size for the header sort glyphs. */
const SORT_ICON_SIZE = 13;

/** The sort-direction glyph for a sortable column header: a bold brand-purple
 *  caret in the active direction, or the muted neutral up/down pair when
 *  unsorted. */
function SortIndicator({ sorted }: { sorted: 'asc' | 'desc' | false }): React.ReactElement {
  if (sorted === 'asc') return <CaretUp weight="bold" size={SORT_ICON_SIZE} className="text-(--brand-500)" />;
  if (sorted === 'desc') return <CaretDown weight="bold" size={SORT_ICON_SIZE} className="text-(--brand-500)" />;
  return <ArrowsDownUp size={SORT_ICON_SIZE} className="text-muted-foreground opacity-50" />;
}

export function DataTableHeader<T>({ table, columns, perBoundaryPx = null, rowSelection, data, getRowId }: DataTableHeaderProps<T>): React.ReactElement {
  const renderHeader = (col: ColumnDef<T>): React.ReactNode =>
    typeof col.header === 'function' ? col.header() : col.header;
  const hasSelectionCol = !!(rowSelection && data && getRowId);
  // Where the first sticky:'left' data column's header cell pins to — right
  // after the sticky checkbox column, matching the body (see DataTable.tsx)
  // so the two rows of pinned cells line up instead of overlapping.
  const selectionWidth = hasSelectionCol ? SELECTION_COL_WIDTH : 0;

  return (
    <TableHeader>
      <TableRow className="h-[40px] bg-stone-50 hover:bg-stone-50">
        {hasSelectionCol && (
          <TableHead
            style={{ width: SELECTION_COL_WIDTH, left: 0 }}
            // Sticky, same as the first data column below — without this the
            // checkbox header scrolls away on its own while the data column
            // pins in place, dragging the checkbox out from under an
            // otherwise-still header row.
            className="sticky z-20 border-b border-border bg-stone-50 px-2"
          >
            <Checkbox
              aria-label="Select all rows"
              checked={selectAllState(rowSelection.selectedIds, selectableIds(data, getRowId, rowSelection.isRowSelectable))}
              onCheckedChange={() =>
                rowSelection.onSelectionChange(
                  toggleSelectAll(rowSelection.selectedIds, selectableIds(data, getRowId, rowSelection.isRowSelectable)),
                )
              }
            />
          </TableHead>
        )}
        {columns.map((col, index) => {
          // Sort state + the asc→desc→clear toggle come from the TanStack column.
          const column = table.getColumn(col.id);
          const sorted = column?.getIsSorted() ?? false; // 'asc' | 'desc' | false
          const thContent = (
            <>
              {renderHeader(col)}
              {col.sortable && (
                <span className="inline-flex" aria-hidden>
                  <SortIndicator sorted={sorted} />
                </span>
              )}
            </>
          );

          const sortButton = col.sortable ? (
            <button
              type="button"
              onClick={column?.getToggleSortingHandler()}
              // w-full + justify: the flex button ignores the th's text-align,
              // so without these a right/center column's label hugs the left.
              className={cn(
                'flex items-center gap-1 cursor-pointer hover:text-foreground transition-colors',
                !col.headerAddon && 'w-full',
                sortButtonAlignClass(col),
              )}
              aria-label={`Sort by ${col.sortLabel ?? (typeof col.header === 'string' ? col.header : col.id)}`}
            >
              {thContent}
            </button>
          ) : null;

          return (
            <TableHead
              key={col.id}
              // Announce sort state to assistive tech on sortable columns
              // ('ascending'/'descending' when active, else 'none'); omitted
              // entirely on non-sortable columns.
              aria-sort={col.sortable ? (sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none') : undefined}
              // Same width logic as the body cells (colWidthStyle) — minWidth
              // floor so an empty/loading table reserves the same spacing as a
              // populated one, and maxWidth honored so a capped grow column
              // can't stretch wide via its header cell alone (table auto-layout
              // sizes columns off the widest cell across header + body).
              // distributedPaddingStyle overrides the CELL_X className once
              // gap-distribution has a real measurement.
              style={{
                ...colWidthStyle(col),
                ...distributedPaddingStyle(perBoundaryPx, index, columns.length),
                // Sits right after the sticky checkbox column (0px when
                // there's no selection column) — see the matching body cell.
                ...(col.sticky === 'left'
                  ? { left: stickyLeftOffset(columns, col, selectionWidth) }
                  : {}),
              }}
              className={cn(
                // Container-staged gap — keep in sync with CELL_X in DataTable.tsx.
                // bg on EVERY header cell (not just the sticky-left one) so the
                // header strip can never go transparent/patchy. The header
                // itself never scrolls vertically (see table.tsx), so unlike
                // the sticky-left column below it needs no z-index — z-index
                // only affects positioned elements, and this cell isn't one.
                'border-b border-border bg-stone-50 px-2 @4xl/dt:px-3 @6xl/dt:px-4 text-[12px] font-medium uppercase text-muted-foreground whitespace-nowrap',
                textAlignClass(col),
                col.sticky === 'left' && 'sticky z-20 bg-stone-50',
                col.sticky === 'right' && 'sticky right-0 z-20 bg-stone-50 in-data-[right-overlap]:shadow-[inset_1px_0_0_var(--border)]',
                // Active sort is signalled by the coloured caret alone; the
                // label keeps the neutral header colour (no brand tint).
                col.headerClassName,
              )}
            >
              {col.sortable ? (
                col.headerAddon ? (
                  <div className={cn('flex w-full items-center gap-1', sortButtonAlignClass(col))}>
                    {sortButton}
                    {col.headerAddon}
                  </div>
                ) : (
                  sortButton
                )
              ) : (
                thContent
              )}
            </TableHead>
          );
        })}
        {needsTrailingSpacer(columns) && perBoundaryPx == null && (
          <TableHead
            aria-hidden
            style={{ width: '100%' }}
            className="border-b border-border bg-stone-50"
          />
        )}
      </TableRow>
    </TableHeader>
  );
}
